import {
  LOCALE_META,
  LOCALES,
  type CalendarPref,
  type DateStyle,
  type DigitPref,
  type Locale,
  type TimeFormat,
} from '@ly360/i18n';
import { ErrorState, PageHead, Select, setLocale, useFormat, ZoneSelect } from '@ly360/ui';
import { useMutation } from '@tanstack/react-query';
import { Check, Info } from 'lucide-react';
import { useEffect, useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { api, type Tenant, type TenantDefaults } from '../lib/api';
import { useAuth } from '../lib/auth';
import { ServiceLevelsSettings } from '../settings/ServiceLevels';

const CALENDARS: CalendarPref[] = ['gregory', 'islamic-umalqura', 'both'];
const NUMERALS: DigitPref[] = ['latn', 'native'];
const CLOCKS: TimeFormat[] = ['12h', '24h'];
const DATE_STYLES: DateStyle[] = ['short', 'medium', 'long'];

function Segmented<T extends string>({
  label,
  value,
  options,
  render,
  onChange,
  inherit,
}: {
  label: string;
  value: T | null;
  options: T[];
  render: (o: T) => string;
  onChange: (v: T | null) => void;
  /** When set, an extra "organisation default" choice maps to null. */
  inherit?: string;
}) {
  return (
    <div className="segmented" role="group" aria-label={label}>
      {inherit && (
        <button aria-pressed={value === null} onClick={() => onChange(null)}>
          {inherit}
        </button>
      )}
      {options.map((o) => (
        <button key={o} aria-pressed={value === o} onClick={() => onChange(o)}>
          {render(o)}
        </button>
      ))}
    </div>
  );
}

function Row({ label, children, htmlFor }: { label: string; children: ReactNode; htmlFor?: string }) {
  return (
    <div className="pref-row">
      <label htmlFor={htmlFor}>
        <strong>{label}</strong>
      </label>
      {children}
    </div>
  );
}

/** My preferences: saved instantly, server-side. Every field can fall back to the organisation default. */
function MyPreferences() {
  const { t, i18n } = useTranslation();
  const { session, savePreferences } = useAuth();
  const [error, setError] = useState<unknown>(null);
  if (!session) return null;
  const p = session.user.preferences;
  const d = session.tenant.defaults;
  const save = (patch: Parameters<typeof savePreferences>[0]) => savePreferences(patch).catch(setError);
  const meta = LOCALE_META[i18n.language as Locale];
  const orgDefault = (v: string) => t('settings.orgDefault', { value: v });

  return (
    <section className="card card-pad" aria-labelledby="my-prefs">
      <h2 id="my-prefs" className="section-title">
        {t('settings.myPreferences')}
      </h2>
      <p className="faint" style={{ marginTop: 0, fontSize: 'var(--t-xs)' }}>
        {t('settings.myPreferencesHint')}
      </p>
      {!!error && <ErrorState error={error} />}
      <Row label={t('settings.language')}>
        <div className="segmented" role="group" aria-label={t('settings.language')}>
          {LOCALES.map((l) => (
            <button
              key={l}
              lang={l}
              aria-pressed={i18n.language === l}
              onClick={() => {
                setLocale(l);
                void save({ locale: l });
              }}
            >
              {LOCALE_META[l].nativeName}
            </button>
          ))}
        </div>
        {meta && !meta.complete && (
          <span
            className="faint"
            style={{ fontSize: 'var(--t-xs)', display: 'flex', gap: 6, alignItems: 'center' }}
          >
            <Info size={14} /> {t('settings.partialTranslation')}
          </span>
        )}
      </Row>
      <Row label={t('settings.timezone')} htmlFor="my-tz">
        <ZoneSelect
          id="my-tz"
          value={p.timezone}
          onChange={(tz) => void save({ timezone: tz })}
          inheritLabel={orgDefault(d.timezone)}
        />
      </Row>
      <Row label={t('settings.clock')}>
        <Segmented
          label={t('settings.clock')}
          value={p.timeFormat}
          options={CLOCKS}
          render={(o) => t(`settings.clockOpt.${o}`)}
          onChange={(v) => void save({ timeFormat: v })}
          inherit={orgDefault(t(`settings.clockOpt.${d.timeFormat}`))}
        />
      </Row>
      <Row label={t('settings.dateStyle')}>
        <Segmented
          label={t('settings.dateStyle')}
          value={p.dateStyle}
          options={DATE_STYLES}
          render={(o) => t(`settings.dateStyleOpt.${o}`)}
          onChange={(v) => void save({ dateStyle: v })}
          inherit={orgDefault(t(`settings.dateStyleOpt.${d.dateStyle}`))}
        />
      </Row>
      <Row label={t('settings.calendar')}>
        <Segmented
          label={t('settings.calendar')}
          value={p.calendar}
          options={CALENDARS}
          render={(o) => t(`settings.calendarOpt.${o}`)}
          onChange={(v) => void save({ calendar: v })}
          inherit={orgDefault(t(`settings.calendarOpt.${d.calendar}`))}
        />
      </Row>
      <Row label={t('settings.numerals')}>
        <Segmented
          label={t('settings.numerals')}
          value={p.numerals}
          options={NUMERALS}
          render={(o) => t(`settings.numeralsOpt.${o}`)}
          onChange={(v) => void save({ numerals: v })}
          inherit={orgDefault(t(`settings.numeralsOpt.${d.numerals}`))}
        />
      </Row>
    </section>
  );
}

/** Organisation defaults: edited as a form and saved together (settings:manage). */
function OrganisationDefaults() {
  const { t } = useTranslation();
  const { session, setTenant } = useAuth();
  const [form, setForm] = useState<TenantDefaults | null>(session?.tenant.defaults ?? null);
  useEffect(() => setForm(session?.tenant.defaults ?? null), [session?.tenant.defaults]);
  const save = useMutation({
    mutationFn: (f: TenantDefaults) =>
      api<{ tenant: Tenant }>('/v1/tenant/settings', {
        method: 'PATCH',
        body: JSON.stringify({
          defaultLocale: f.locale,
          timezone: f.timezone,
          timeFormat: f.timeFormat,
          dateStyle: f.dateStyle,
          calendar: f.calendar,
          numerals: f.numerals,
          weekStart: f.weekStart,
        }),
      }),
    onSuccess: (r) => setTenant(r.tenant),
  });
  if (!form) return null;
  const set = <K extends keyof TenantDefaults>(k: K, v: TenantDefaults[K]) => setForm({ ...form, [k]: v });

  return (
    <section className="card card-pad" aria-labelledby="org-defaults" data-testid="org-defaults">
      <h2 id="org-defaults" className="section-title">
        {t('settings.organisation')}
      </h2>
      <p className="faint" style={{ marginTop: 0, fontSize: 'var(--t-xs)' }}>
        {t('settings.organisationHint')}
      </p>
      <div className="form-grid">
        <div className="field">
          <label htmlFor="org-tz">{t('settings.timezone')}</label>
          <ZoneSelect id="org-tz" value={form.timezone} onChange={(tz) => tz && set('timezone', tz)} />
        </div>
        <div className="field">
          <label htmlFor="org-locale">{t('settings.defaultLanguage')}</label>
          <Select
            id="org-locale"
            value={form.locale}
            onChange={(v) => set('locale', v)}
            options={[
              { value: 'en', label: 'English' },
              { value: 'ar', label: 'العربية' },
            ]}
          />
        </div>
        <div className="field">
          <label htmlFor="org-clock">{t('settings.clock')}</label>
          <Select
            id="org-clock"
            value={form.timeFormat}
            onChange={(v) => set('timeFormat', v as TimeFormat)}
            options={CLOCKS.map((c) => ({ value: c, label: t(`settings.clockOpt.${c}`) }))}
          />
        </div>
        <div className="field">
          <label htmlFor="org-date">{t('settings.dateStyle')}</label>
          <Select
            id="org-date"
            value={form.dateStyle}
            onChange={(v) => set('dateStyle', v as DateStyle)}
            options={DATE_STYLES.map((c) => ({ value: c, label: t(`settings.dateStyleOpt.${c}`) }))}
          />
        </div>
        <div className="field">
          <label htmlFor="org-cal">{t('settings.calendar')}</label>
          <Select
            id="org-cal"
            value={form.calendar}
            onChange={(v) => set('calendar', v as CalendarPref)}
            options={CALENDARS.map((c) => ({ value: c, label: t(`settings.calendarOpt.${c}`) }))}
          />
        </div>
        <div className="field">
          <label htmlFor="org-num">{t('settings.numerals')}</label>
          <Select
            id="org-num"
            value={form.numerals}
            onChange={(v) => set('numerals', v as DigitPref)}
            options={NUMERALS.map((c) => ({ value: c, label: t(`settings.numeralsOpt.${c}`) }))}
          />
        </div>
        <div className="field">
          <label htmlFor="org-week">{t('settings.weekStart')}</label>
          <Select
            id="org-week"
            value={String(form.weekStart)}
            onChange={(v) => set('weekStart', Number(v))}
            options={[0, 1, 6].map((d) => ({ value: String(d), label: t(`days.${d}`) }))}
          />
        </div>
      </div>
      {save.isError && <ErrorState error={save.error} />}
      <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
        <button className="btn btn-grad" onClick={() => save.mutate(form)} disabled={save.isPending}>
          {save.isPending ? t('common.saving') : t('common.save')}
        </button>
        {save.isSuccess && (
          <span
            className="faint"
            role="status"
            style={{ display: 'inline-flex', gap: 6, alignItems: 'center' }}
          >
            <Check size={16} color="var(--success)" /> {t('settings.saved')}
          </span>
        )}
      </div>
    </section>
  );
}

function Preview() {
  const { t } = useTranslation();
  const { session } = useAuth();
  const fmt = useFormat();
  const [now, setNow] = useState(() => new Date());
  useEffect(() => {
    const i = setInterval(() => setNow(new Date()), 30_000);
    return () => clearInterval(i);
  }, []);
  return (
    <aside className="panel-dark card-pad" aria-label={t('settings.preview')}>
      <div className="eyebrow" style={{ color: 'var(--tx-on-dark-faint)' }}>
        {t('settings.preview')}
      </div>
      <div className="pref-row" style={{ borderColor: 'var(--line-dark-soft)' }}>
        <span className="faint">{t('settings.currentTime')}</span>
        <span className="preview-val" data-testid="preview-time">
          {fmt.timeZoned(now)}
        </span>
        <span className="faint mono" style={{ fontSize: 'var(--t-xs)' }}>
          {fmt.timezone}
        </span>
      </div>
      <div className="pref-row" style={{ borderColor: 'var(--line-dark-soft)' }}>
        <span className="faint">{t('settings.previewDate')}</span>
        <span className="preview-val" data-testid="preview-date">
          {fmt.date(now)}
        </span>
      </div>
      <div className="pref-row" style={{ borderColor: 'var(--line-dark-soft)' }}>
        <span className="faint">{t('settings.previewMoney')}</span>
        <span className="preview-val">{fmt.money(1249950, session?.tenant.currency ?? 'AED')}</span>
      </div>
    </aside>
  );
}

export function SettingsPage() {
  const { t } = useTranslation();
  const { can } = useAuth();
  return (
    <>
      <PageHead eyebrow={t('nav.settings')} title={t('settings.title')} subtitle={t('settings.subtitle')} />
      <div className="prefs-grid">
        <div style={{ display: 'grid', gap: '1rem', minWidth: 0 }}>
          <MyPreferences />
          {can('settings:manage') && <OrganisationDefaults />}
          {can('settings:manage') && <ServiceLevelsSettings />}
        </div>
        <Preview />
      </div>
    </>
  );
}
